import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from '../../components/Picker/DatePicker';
import OrderItem from '../../components/Order/OrderItem';
import OrderPayment from '../../components/Order/OrderPayment';
import EquipmentRatingPopup from '../../components/Popup/EquipmentRatingPopup';
import dayjs from 'dayjs';
import o_select from "../../assets/icon/order_select.svg";
import o_order from "../../assets/icon/order_.svg";
import o_receive from "../../assets/icon/order_receive.svg";
import o_rental from "../../assets/icon/order_rental.svg";
import o_return from "../../assets/icon/order_return.svg";
import next from "../../assets/icon/16_next_gray.svg";
import product from "../../assets/img/detailproduct.png";
import DaumPostcodeEmbed from 'react-daum-postcode';
import orderApi from "../../services/Order/orderApi";
import PaymentService from "../../services/Payment/PaymentService";
import { debounce } from 'lodash';
import { formatLocalString } from "../../utils/validation";
import AddressSelectPopup from "../../components/Popup/AddressSelectPopup";
import { useLanguage } from "../../hooks/useLanguage";
import PopupLoginError from "../../components/Popup/LoginError";
import useUserStore from "../../store/userStore";
import { useOrderCount } from './OrderView';
import ProductStockPop from "../../components/Popup/ProductStockPopup";
import TermsPopup from "../../components/Popup/TermsPopup";
import { TERMS_LIST_URL, FOOTER_DATA_URL } from "../../utils/urls";
import instance from "../../services/services";

// 디바운스된 API 호출 함수 생성 (컴포넌트 외부에)
const debouncedFn = debounce((callback) => {
  callback();
}, 300);

function ParcelOrder(){
  const { t } = useLanguage();
  const { memberTokens, userInfo } = useUserStore();
  const navigate = useNavigate();
  const [memo, setMemo] = useState('');
  const [isShow, setIsShow] = useState(false); // 패키지 보기 버튼
  const [selectedDelivery, setSelectedDelivery] = useState('new'); // 배송지 선택
  const [selectedMessage, setSelectedMessage] = useState(''); // 배송 요청사항
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [cartListData, setCartListData] = useState({
    bundleMain: [],
    bundleCp: [], // bundleAdd에서 bundleCp로 변경
    bundleAcc: []
  });
  const [deliveryData, setDeliveryData] = useState([]);
  const [isPopupAddr,setPopupAddr] = useState(false);
  const [isPopupLoginError, setIsPopupLoginError] = useState("");
  // 우편번호 관련 상태 추가
  const [isPostcodeOpen, setIsPostcodeOpen] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState({
    zonecode: '',
    roadAddress: '',
    detailAddress: ''
  });

  // 상태 추가
  const [recipientInfo, setRecipientInfo] = useState({
    name: '',
    phone: ''
  });

  // 장비평점 팝업
  const [isEquipmentPopup, setEquipmentPopup ] = useState(false);
  const handleEquipmentPopup = () => {
    setEquipmentPopup(true)
  };

  // Context 사용
  const { refreshOrderCount } = useOrderCount();

  // 배송지 선택
  const handleDeliveryChange = (e) => {
    const newValue = e.target.value;
    setSelectedDelivery(newValue);

    // '새로입력' 선택 시 모든 필드 초기화
    if (newValue === 'new') {
      // 배송지 주소 초기화
      setDeliveryAddress({
        zonecode: '',
        roadAddress: '',
        detailAddress: ''
      });

      // 받는 사람 정보 초기화
      setRecipientInfo({
        name: '',
        phone: ''
      });

      // 배송 메시지 초기화
      setSelectedMessage('');
      setCustomMessage('');
    } else {
      // 기본주소 선택 시 기존 배송지 정보로 복원
      setDeliveryAddress({
        zonecode: deliveryData.recipient_zipcode || '',
        roadAddress: deliveryData.recipient_address_street || '',
        detailAddress: deliveryData.recipient_address_detail || ''
      });

      setRecipientInfo({
        name: deliveryData.recipient_name || '',
        phone: deliveryData.recipient_phone || ''
      });
    }
  };

  // 배송지 정보
  const deliveryLIst = [
    {id:'default_addr', label:t('default_address'), value:'default'},
    {id:'new_addr', label:t('input_new_address'), value:'new'}
  ]

  //배송 요청사항
  const messageList = [
    { name:'부재 시 문 앞에 놓아주세요.', value:'reason1' },
    { name:'부재 시 경비실에 맡겨주세요.', value:'reason2' },
    { name:'부재 시 전화 또는 문자 주세요.', value:'reason3' },
    { name:'파손의 위험이 있는 상품입니다. 배송 시 주의해 주세요.', value:'reason4' },
    { name:'배송 전에 연락 주세요.', value:'reason5' },
    { name:'직접입력', value:'reason6' },
  ]

  // 대여 날짜/시간 상태 추가
  const [rentalDateTime, setRentalDateTime] = useState({
    date: dayjs().add(3, 'day'), // 오늘로부터 3일 후
    time: ''
  });

  // 반납 날짜/시간 상태 추가
  const [returnDateTime, setReturnDateTime] = useState({
    date: dayjs().add(3, 'day'), // 대여 시작일과 동일하게 설정
    time: ''
  });

  // 총 대여일수 계산 (새로 추가)
  const [totalRentalDays, setTotalRentalDays] = useState(0);

  // 대여 날짜 변경 핸들러
  const handleRentalDateChange = (newDate) => {
    // 대여 날짜와 반납 날짜를 모두 같은 날짜로 설정
    setRentalDateTime(prev => ({ ...prev, date: newDate }));
    setReturnDateTime(prev => ({ ...prev, date: newDate }));

    // 대여일수 계산 (같은 날짜로 설정하므로 1일)
    setTotalRentalDays(1);

    // 결제 폼 초기화
    resetPaymentForm();

    // API 호출
    debouncedFn(() => {
      const params = {
        ...createRequestParams(),
        rental_sdate: newDate.format('YYYY-MM-DD'),
        rental_edate: newDate.format('YYYY-MM-DD')
      };

      orderApi.getCartDetail(params)
        .then(response => {
          if (response.data.code === 200) {
            const data = response.data.data;
            // 기존 체크 상태 유지하면서 데이터 업데이트
            setCartListData(prevCart => ({
              bundleMain: data.cartList?.bundleMain?.map(item => ({
                ...item,
                isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
              })) || [],
              bundleCp: data.cartList?.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
                ...item,
                isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
              })) || [],
              bundleAcc: data.cartList?.bundleAcc?.map(item => ({
                ...item,
                isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
              })) || []
            }));
            setOrderSummary(data);
          }
        })
        .catch(error => {
          console.error('대여 날짜 변경 중 오류:', error);
        });
    });
  };

  // 반납 날짜 변경 핸들러
  const handleReturnDateChange = (newDate) => {
    setReturnDateTime(prev => ({ ...prev, date: newDate }));
    calculateRentalDays(rentalDateTime.date, newDate);

    // 결제 폼 초기화
    resetPaymentForm();

    // API 호출
    debouncedFn(() => {
      const params = {
        ...createRequestParams(),
        rental_sdate: rentalDateTime.date.format('YYYY-MM-DD'),
        rental_edate: newDate.format('YYYY-MM-DD')
      };

      orderApi.getCartDetail(params)
        .then(response => {
          if (response.data.code === 200) {
            const data = response.data.data;
            // 기존 체크 상태 유지하면서 데이터 업데이트
            setCartListData(prevCart => ({
              bundleMain: data.cartList?.bundleMain?.map(item => ({
                ...item,
                isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
              })) || [],
              bundleCp: data.cartList?.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
                ...item,
                isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
              })) || [],
              bundleAcc: data.cartList?.bundleAcc?.map(item => ({
                ...item,
                isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
              })) || []
            }));
            setOrderSummary(data);
          }
        })
        .catch(error => {
          console.error('반납 날짜 변경 중 오류:', error);
        });
    });
  };

  // 대여일수 계산 함수 (새로 추가)
  const calculateRentalDays = (startDate, endDate) => {
    // 날짜 차이 계산 (밀리초 -> 일)
    const start = startDate.startOf('day');
    const end = endDate.startOf('day');
    const diffDays = end.diff(start, 'day');

    // 대여 기간은 시작일과 종료일을 모두 포함
    const rentalDays = diffDays + 1;
    setTotalRentalDays(rentalDays);
  };

  // 컴포넌트 마운트 시 초기 대여일수 계산
  useEffect(() => {
    calculateRentalDays(rentalDateTime.date, returnDateTime.date);
  }, [rentalDateTime.date, returnDateTime.date]);

  // 패키지 보기 버튼
  const handleShow = (packageId) => {
    setIsShow(prev => ({
      ...prev,
      [packageId]: !prev[packageId]
    }));
  };

  // 요청사항
  const handleMemoChange = (e) => {
    setMemo(e.target.value);
  };

  const [isPayment, setIsPayment] = useState(false);

  // 결제 전 유효성 검사
  const orderInfoValidation = () => {
    const validation = PaymentService.validatePaymentForm({
      deliveryInfo: {
        recipient_zipcode: deliveryAddress.zonecode,
        recipient_address_street: deliveryAddress.roadAddress,
      },
      // 대여 기간 정보 추가
      rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')} 12:00:00`,
      rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')} 12:00:00`,
    });

    if (!validation.isValid) {
      setIsPopupLoginError(validation.errors[0]);
      return false;
    }

    return true;
  };

  // 결제하기 핸들러
  const handlePayment = async () => {
    // 선택된 상품이 있는지 확인
    if (!selectedProductIds || selectedProductIds.length === 0) {
      setIsPopupLoginError(t('select_payment_product'));
        return;
    }

    // 필수 약관 동의 확인
    if (!Object.values(isChecked).every(Boolean)) {
      setIsPopupLoginError(t('agree_required_terms'));
        return;
    }

    const isValid = orderInfoValidation();
    if (!isValid) return;

    // 결제 요청 데이터 준비
    const paymentForm = {
        order_seq: "",
        reorder_chk: "N",
        order_type: "parcel",
        rental_store_seq: "",
        return_store_seq: "",
        rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')}`,
        rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')}`,
        download_seq: requestParams.maxCouponUse === 'Y'
            ? orderSummary?.totalInfo?.maxDiscountCouponSeq
            : requestParams.download_seq || "",
        point: requestParams.point || "",
        balance: requestParams.balance || "",
        shipping_cost: orderSummary?.totalInfo?.delivery_price || 0,
        deposit_price: orderSummary?.totalInfo?.deposit_price || 0,
        balance_cash_receipt: requestParams.deposit_cash_receipt || 'N',
        balance_cash_num: requestParams.deposit_cash_num || '',      // 현금영수증 번호

        depositInfo: {
            deposit_refund_account: "",
            deposit_refund_mname: "",
            deposit_refund_name: "",
            deposit_refund_price: orderSummary?.totalInfo?.deposit_price || 0,
        },
        discountInfo: {
          first_payment_sale: orderSummary?.totalInfo?.first_payment_sale || 0,
          mem_group_sale: orderSummary?.totalInfo?.sumGroupSale || 0,
          mem_type_sale: orderSummary?.totalInfo?.sumMtypeSale || 0
        },
        deliveryInfo: {
            recipient_name: selectedDelivery === 'default' ?
                deliveryData.recipient_name :
                recipientInfo.name,
            recipient_phone: selectedDelivery === 'default' ?
                deliveryData.recipient_phone :
                recipientInfo.phone,
            recipient_zipcode: deliveryAddress.zonecode,
            recipient_address_street: deliveryAddress.roadAddress,
            recipient_address_detail: deliveryAddress.detailAddress,
            recipient_memo: selectedMessage === 'reason6' ?
                customMessage :
                messageList.find(msg => msg.value === selectedMessage)?.name || ""
        },

        insuranceInfo: {
            insurance_seq: requestParams.insurance_seq || 0,
            insurance_repair_per: orderSummary?.insuranceList?.find(
                insurance => insurance.insurance_seq === requestParams.insurance_seq
            )?.insurance_repair_per || 0,
            insurance_price: orderSummary?.totalInfo?.insurance_price || 0
        },

        user_memo: memo,
        // cart_select_goods: [],  // 빈 배열로 설정
        pay_select_goods: selectedProductIds.filter(id => {
            // bundleMain, bundleAdd, bundleAcc에서 체크된 상품만 필터링
            const isMainChecked = cartListData.bundleMain?.some(item =>
                item.cart_option_seq === id && item.isChecked);
            const isAddChecked = cartListData.bundleCp?.some(item =>
                item.cart_option_seq === id && item.isChecked);
            const isAccChecked = cartListData.bundleAcc?.some(item =>
                item.cart_option_seq === id && item.isChecked);
            return isMainChecked || isAddChecked || isAccChecked;
        })
    };

    try {
      const result = await PaymentService.requestPayment(paymentForm);


      if (result.success) {
        const data = result.data;

        // 0원 결제 처리 (문자열 "0"과 숫자 0 모두 처리)
        if (Number(data.price) === 0) {
            const zeroPaymentData = PaymentService.createZeroPaymentData(data);
            setIsPopupLoginError('주문이 완료되었습니다.\n주문정보는 마이페이지에서 확인 가능합니다.');
            navigate(`/order/orderComplete/${zeroPaymentData.orderSeq}`, {
                state: {
                    order_seq: zeroPaymentData.orderSeq,
                    settleprice: zeroPaymentData.settleprice,
                    order_kind: zeroPaymentData.orderKind
                }
            });
            return;
        }

        if (result.isMobile) {
            // 모바일/앱에서는 리다이렉션 방식 사용
            PaymentService.processMobilePayment(data, window.location.origin);
        } else {
            // PC에서는 iframe 방식 사용
            setIsPayment(true);

            setTimeout(() => {
                const iframe = document.getElementById('payment-container');
                if (iframe) {
                    const paymentUrl = PaymentService.createPaymentUrl(data, window.location.origin);
                    iframe.src = paymentUrl;
                } else {
                    console.error('❌ 결제창 iframe을 찾을 수 없습니다.');
                    setIsPopupLoginError('결제창을 열 수 없습니다. 페이지를 새로고침 후 다시 시도해주세요.');
                    setIsPayment(false);
                }
            }, 100);
        }
      } else {
        console.error('❌ 결제 요청 실패:', result);

        // 결제 실패 시 재고부족 팝업 처리
        if (result.data?.code === 500 && result.data?.data && result.data.data.length > 0) {
          setStockData(result.data); // 전체 데이터 전달
          setStockPopup(true);
          return;
        } else {
          setIsPopupLoginError(result?.data?.message || '결제 요청에 실패했습니다.');
        }
        setIsPayment(false);
      }
    } catch (error) {
      console.error('결제 요청 중 오류:', error);
      setIsPopupLoginError('결제 처리 중 오류가 발생했습니다.');
      setIsPayment(false);
    }
  };

    // 재고 부족 항목이 있는 패키지 자동 펼치기
    useEffect(() => {
      if (cartListData && (cartListData.bundleMain || cartListData.bundleCp || cartListData.bundleAcc)) {
        const newIsShow = {};

        // bundleMain에서 bundleCp와 bundlePkg를 확인
        if (cartListData.bundleMain && cartListData.bundleMain.length > 0) {
          cartListData.bundleMain.forEach(item => {
            let hasStockShortage = false;

            // bundleCp 중에서 stock_flag가 'N'인 항목이 있는지 확인
            if (item.bundleCp && item.bundleCp.length > 0) {
              hasStockShortage = item.bundleCp.some(cpItem => cpItem.stock_flag === 'N');
            }

            // bundlePkg 중에서 stock_flag가 'N'인 항목이 있는지 확인
            if (!hasStockShortage && item.bundlePkg && item.bundlePkg.length > 0) {
              hasStockShortage = item.bundlePkg.some(pkgItem => pkgItem.stock_flag === 'N');
            }

            if (hasStockShortage) {
              newIsShow[item.cart_option_seq] = true; // 재고 부족 항목이 있으면 펼치기
            }
          });
        }

        // 기존 상태와 병합 (기존에 수동으로 열린 항목들은 유지)
        setIsShow(prev => ({
          ...prev,
          ...newIsShow
        }));
      }
    }, [cartListData]);

  // 결제 완료 메시지 처리
  useEffect(() => {
    const handlePaymentMessage = (e) => {
      const result = PaymentService.handlePaymentMessage(e);

      if (!result) return; // 처리할 메시지가 아님

      if (result.success) {
        setIsPopupLoginError('주문이 완료되었습니다.\n주문정보는 마이페이지에서 확인 가능합니다.');

        // 결제 완료 후 페이지 이동
        navigate(`/order/orderComplete/${result.orderSeq}`, {
          state: {
            order_seq: result.orderSeq,
            settleprice: result.settleprice,
            order_kind: result.orderKind
          }
        });
      } else {
        setIsPopupLoginError(result.message || '주문이 실패하였습니다.');
      }
    };

    window.addEventListener('message', handlePaymentMessage);
    return () => window.removeEventListener('message', handlePaymentMessage);
  }, [navigate]);

  // 우편번호 찾기 핸들러
  const handlePostcodeOpen = () => {
    setIsPostcodeOpen(true);
  };

  const handlePostcodeComplete = (data) => {
    setDeliveryAddress({
      zonecode: data.zonecode,
      roadAddress: data.roadAddress,
      detailAddress: ''
    });
    setIsPostcodeOpen(false);
  };

  const handlePostcodeClose = () => {
    setIsPostcodeOpen(false);
  };

  // isAllChecked 상태 초기값을 false로 설정
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [isChecked, setIsChecked] = useState({
      option1: false,
      option2: false,
      option3: false,
      option4: false,
  });

  const handleAllCheck = () => {
      setIsAllChecked(!isAllChecked);
      const newCheckedState = {
          option1: !isAllChecked,
          option2: !isAllChecked,
          option3: !isAllChecked,
          option4: !isAllChecked,
      };
      setIsChecked(newCheckedState);
  };

  const handleCheck = (option) => {
      const newCheckedState = { ...isChecked, [option]: !isChecked[option] };
      setIsChecked(newCheckedState);
      setIsAllChecked(Object.values(newCheckedState).every(Boolean));
  };

  const handleReset = () => {
    window.location.reload();
  }

  // 결제 폼 초기화 함수
  const [paymentResetKey, setPaymentResetKey] = useState(0);
  const resetPaymentForm = () => {
    setPaymentResetKey(prev => prev + 1);
  };

  // 배송지 목록
  useEffect(() => {
    const fetchDeliveryList = async () => {
      try {
        const response = await orderApi.getDefaultDeliveryInfo();
        setDeliveryData(response.data.data);

        // default_flag 값이 'Y'인 경우 기본주소 선택
        if (response.data.data.default_flag === 'Y') {
          setSelectedDelivery('default');
        } else {
          setSelectedDelivery('new');
        }
      } catch (error) {
        console.error('배송지 목록 조회 중 오류:', error);
        // 에러 발생 시 새로입력 선택
        setSelectedDelivery('new');
      }
    };
    fetchDeliveryList();
  }, []);

  // useEffect 추가 - deliveryData가 변경될 때 기본주소 정보 설정
  useEffect(() => {
    if (selectedDelivery === 'default' && deliveryData) {
      setDeliveryAddress({
        zonecode: deliveryData.recipient_zipcode || '',
        roadAddress: deliveryData.recipient_address_street || '',
        detailAddress: deliveryData.recipient_address_detail || ''
      });
    } else if (selectedDelivery === 'new') {
      setDeliveryAddress({
        zonecode: '',
        roadAddress: '',
        detailAddress: ''
      });
    }
  }, [selectedDelivery, deliveryData]);

  // 컴포넌트 마운트 시 모바일 결제 결과 확인 (수정)
  useEffect(() => {

    // URL 파라미터에서 결제 결과 확인
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const oid = urlParams.get('oid');
    const resultCode = urlParams.get('resultCode');


    // 결제 성공 파라미터가 있는 경우
    if ((status === 'succ' || resultCode === '0000') && oid) {

        const price = urlParams.get('price') || urlParams.get('P_AMT') || '0';
        const orderKind = urlParams.get('order_kind') || 'parcel';

        // URL에서 결제 관련 파라미터 제거
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);

        // 결제 성공 처리
        setIsPopupLoginError('주문이 완료되었습니다.\n주문정보는 마이페이지에서 확인 가능합니다.');
        navigate(`/order/orderComplete/${oid}`, {
            state: {
                order_seq: oid,
                settleprice: price,
                order_kind: orderKind
            },
            replace: true
        });
        return;
    }

    // 기존 로직 (localStorage 확인)
    const paymentResult = PaymentService.checkMobilePaymentResult();
    if (paymentResult) {

        if (paymentResult.success) {
          setIsPopupLoginError('주문이 완료되었습니다.\n주문정보는 마이페이지에서 확인 가능합니다.');
            navigate(`/order/orderComplete/${paymentResult.orderSeq}`, {
                state: {
                    order_seq: paymentResult.orderSeq,
                    settleprice: paymentResult.settleprice,
                    order_kind: paymentResult.orderKind
                },
                replace: true
            });
        } else {
          setIsPopupLoginError(paymentResult.message || '주문이 실패하였습니다.');
        }
    }
}, [navigate]);

  // 상품 데이터 상태 추가
  const [orderSummary, setOrderSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // 선택된 상품 IDs 상태 추가
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  // 선택된 아이템 카운트를 위한 상태 추가
  const [selectedCounts, setSelectedCounts] = useState({
    main: 0,
    add: 0,
    acc: 0
  });

  // useRef 추가
  const selectedProductIdsRef = useRef([]);

  // selectedProductIds가 변경될 때마다 ref 업데이트
  useEffect(() => {
    selectedProductIdsRef.current = selectedProductIds;
  }, [selectedProductIds]);

  // didSetInitialSelection ref 추가
  const didSetInitialSelection = useRef(false);

  // 컴포넌트 마운트 시 초기 데이터 설정 useEffect 수정
  useEffect(() => {
    if (
      !didSetInitialSelection.current &&
      cartListData &&
      Object.keys(cartListData).length > 0 &&
      selectedProductIds.length === 0
    ) {
      const main = cartListData.bundleMain || [];
      const add = cartListData.bundleCp || [];
      const acc = cartListData.bundleAcc || [];

      const mainIds = main.map(item => item.cart_option_seq);
      const addIds = add.map(item => item.cart_option_seq);
      const accIds = acc.map(item => item.cart_option_seq);

      // 초기 로딩시에만 isChecked: true 세팅
      setCartListData(prevCart => ({
        ...cartListData,
        bundleMain: main.map(item => ({ ...item, isChecked: true })),
        bundleCp: add.map(item => ({ ...item, isChecked: true })),
        bundleAcc: acc.map(item => ({ ...item, isChecked: true }))
      }));

      setSelectedProductIds([...mainIds, ...addIds, ...accIds]);
      setSelectedCounts({
        main: main.length,
        add: add.length,
        acc: acc.length
      });

      didSetInitialSelection.current = true; // 초기 설정 완료 표시
    }
  }, [cartListData, selectedProductIds.length]);

  // requestParams 상태 추가
  const [requestParams, setRequestParams] = useState({
    point: "",
    allPointUse: "N",
    coupon_seq: "",
  });

  // createRequestParams를 먼저 정의
  const createRequestParams = useCallback((selectedIds) => {
    return {
        order_kind: "parcel",
        rental_store_seq: "",
        return_store_seq: "",
        rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')}`,
        rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')}`,
        point: requestParams.point || "",
        allPointUse: requestParams.allPointUse || "N",
        download_seq: requestParams.download_seq || "",
        balance: requestParams.balance || "",
        allBalanceUse: requestParams.allBalanceUse || "N",
        cart_select_goods: selectedIds || selectedProductIds,
        pay_select_goods: [],
        undecidedChk: "N",
        insurance_seq: requestParams.insurance_seq || "",
    };
  }, [rentalDateTime.date, returnDateTime.date, selectedProductIds, requestParams]);

  const handleUpdateParams = useCallback((updates) => {
    setRequestParams((prev) => {
      const updated = { ...prev, ...updates };

      // API 호출
      const apiParams = {
        ...createRequestParams(updated.cart_select_goods),
        ...updated,
      };

      debouncedFn(() => {
        orderApi.getCartDetail(apiParams)
          .then((response) => {
            if (response.data.code === 200) {
              const data = response.data.data;
              setCartListData({
                bundleMain: data.cartList?.bundleMain?.map(item => ({
                  ...item,
                  isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
                })) || [],
                bundleCp: data.cartList?.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
                  ...item,
                  isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
                })) || [],
                bundleAcc: data.cartList?.bundleAcc?.map(item => ({
                  ...item,
                  isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
                })) || [],
              });
              setOrderSummary(data);
            }
          })
          .catch(error => {
            console.error('파라미터 업데이트 중 오류:', error);
          });
      });

      return updated;
    });
  }, [createRequestParams]);

  const handleProductSelect = useCallback((productId, isSelected, type = 'main') => {
    // 대여상품(bundleMain)인 경우 cart_seq가 동일한 부가품목과 액세서리도 함께 처리
    if (type === 'main') {
      // 체크/해제된 대여상품의 cart_seq 찾기
      const mainItem = cartListData.bundleMain.find(item => item.cart_option_seq === productId);

      if (mainItem && mainItem.cart_seq) {
        const mainCartSeq = mainItem.cart_seq;

        // 해당 cart_seq를 가진 부가품목과 액세서리 찾기
        const relatedAddItems = cartListData.bundleCp.filter(item => item.cart_seq === mainCartSeq);
        const relatedAccItems = cartListData.bundleAcc.filter(item => item.cart_seq === mainCartSeq);

        // 관련 부가품목과 액세서리의 cart_option_seq 목록 생성
        const relatedIds = [
          ...relatedAddItems.map(item => item.cart_option_seq), // bundleAdd에서 bundleCp로 변경
          ...relatedAccItems.map(item => item.cart_option_seq)
        ];

        // UI 업데이트 - 대여상품과 관련 부가품목/액세서리 모두 반영
        setCartListData(prevCart => ({
          bundleMain: prevCart.bundleMain.map(item => ({
            ...item,
            isChecked: item.cart_option_seq === productId ? isSelected : item.isChecked
          })),
          bundleCp: prevCart.bundleCp.map(item => ({ // bundleAdd에서 bundleCp로 변경
            ...item,
            // 관련 아이템일 경우 대여상품과 동일한 체크 상태로 설정
            isChecked: relatedIds.includes(item.cart_option_seq) ? isSelected : item.isChecked
          })),
          bundleAcc: prevCart.bundleAcc.map(item => ({
            ...item,
            // 관련 아이템일 경우 대여상품과 동일한 체크 상태로 설정
            isChecked: relatedIds.includes(item.cart_option_seq) ? isSelected : item.isChecked
          }))
        }));

        // selectedProductIds 업데이트
        setSelectedProductIds(prev => {
          let updated;

          if (isSelected) {
            // 체크 시: 대여상품과 관련 아이템 모두 추가
            updated = [...new Set([...prev, productId, ...relatedIds])];
          } else {
            // 체크 해제 시: 대여상품과 관련 아이템 모두 제거
            const idsToRemove = [productId, ...relatedIds];
            updated = prev.filter(id => !idsToRemove.includes(id));
          }

          // API 호출
          const params = createRequestParams(updated);
          debouncedFn(() => {
            orderApi.getCartDetail(params)
              .then(response => {
                if (response.data.code === 200) {
                  const data = response.data.data;
                  setOrderSummary(data);
                }
              })
              .catch(error => {
                console.error('상품 선택 업데이트 중 오류:', error);
              });
          });

          return updated;
        });

        return; // 여기서 함수 종료
      }
    }

    // 부가품목/액세서리 체크 로직 - 관련 대여상품이 체크되어 있는지 확인
    if ((type === 'add' || type === 'acc') && isSelected) {
      const item = type === 'add'
        ? cartListData.bundleCp.find(item => item.cart_option_seq === productId) // bundleAdd에서 bundleCp로 변경
        : cartListData.bundleAcc.find(item => item.cart_option_seq === productId);

      if (item && item.cart_seq) {
        // 관련 대여상품 찾기
        const relatedMainItems = cartListData.bundleMain.filter(mainItem =>
          mainItem.cart_seq === item.cart_seq
        );

        // 관련 대여상품 중 하나라도 체크되어 있는지 확인
        const isAnyMainItemChecked = relatedMainItems.some(mainItem => mainItem.isChecked);

        if (!isAnyMainItemChecked) {
          setIsPopupLoginError(t('select_related_rental_product'));
          return; // 체크 처리하지 않고 함수 종료
        }
      }
    }

    // 기존 로직 (일반적인 경우 처리)
    setSelectedProductIds(prev => {
      const updated = isSelected
        ? [...new Set([...prev, productId])]
        : prev.filter(id => id !== productId);

      const updateCartListData = (prevCart) => {
        const updateItems = (items) =>
          items?.map(item => ({
            ...item,
            isChecked: updated.includes(item.cart_option_seq)
          })) || [];

        return {
          bundleMain: updateItems(prevCart.bundleMain),
          bundleCp: updateItems(prevCart.bundleCp), // bundleAdd에서 bundleCp로 변경
          bundleAcc: updateItems(prevCart.bundleAcc)
        };
      };

      setCartListData(prevCart => updateCartListData(prevCart));

      // API 호출
      const params = createRequestParams(updated);
      debouncedFn(() => {
        orderApi.getCartDetail(params)
          .then(response => {
            if (response.data.code === 200) {
              const data = response.data.data;
              setOrderSummary(data);
            }
          })
          .catch(error => {
            console.error('상품 선택 업데이트 중 오류:', error);
          });
      });

      return updated;
    });
  }, [cartListData, createRequestParams, t]);

  // fetchCartDetail 함수 수정
  const fetchCartDetail = useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);

    try {
      const params = createRequestParams();
      const response = await orderApi.getCartDetail(params);

      if (response.data.code === 200) {
        const data = response.data.data;

        // selectedProductIds가 비어있는 경우 모든 isChecked를 false로 설정
        setCartListData(prevCart => ({
          bundleMain: data.cartList.bundleMain?.map(item => ({
            ...item,
            isChecked: selectedProductIdsRef.current.length > 0
              ? selectedProductIdsRef.current.includes(item.cart_option_seq)
              : false
          })) || [],
          bundleCp: data.cartList.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
            ...item,
            isChecked: selectedProductIdsRef.current.length > 0
              ? selectedProductIdsRef.current.includes(item.cart_option_seq)
              : false
          })) || [],
          bundleAcc: data.cartList.bundleAcc?.map(item => ({
            ...item,
            isChecked: selectedProductIdsRef.current.length > 0
              ? selectedProductIdsRef.current.includes(item.cart_option_seq)
              : false
          })) || []
        }));

        setOrderSummary(data);
      }
    } catch (error) {
      console.error('장바구니 상세 조회 중 오류:', error);
    } finally {
      setIsLoading(false);
    }
  }, [createRequestParams, isLoading]);

  // 새로운 useEffect 추가 - 선택된 아이템 카운트 업데이트
  useEffect(() => {
    const countCheckedItems = (items = []) => items.filter(item => item.isChecked).length;

    setSelectedCounts({
      main: countCheckedItems(cartListData.bundleMain),
      add: countCheckedItems(cartListData.bundleCp), // bundleAdd에서 bundleCp로 변경
      acc: countCheckedItems(cartListData.bundleAcc)
    });
  }, [cartListData]);

  // 상품 수량 변경 핸들러 수정
  const handleProductQuantityChange = (changeData) => {
    const params = {
      ...createRequestParams(),
      cart_change_goods: [changeData]
    };

    debouncedFn(() => {
      orderApi.getCartDetail(params)
        .then(response => {
          if (response.data.code === 200) {
            const data = response.data.data;

            setCartListData({
              bundleMain: data.cartList.bundleMain?.map(item => ({
                ...item,
                isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
              })) || [],
              bundleCp: data.cartList.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
                ...item,
                isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
              })) || [],
              bundleAcc: data.cartList.bundleAcc?.map(item => ({
                ...item,
                isChecked: selectedProductIdsRef.current.includes(item.cart_option_seq)
              })) || [],
            });

            setOrderSummary(data);
          }
        })
        .catch(error => {
          console.error('상품 수량 변경 중 오류:', error);
        });
    });
  };

  // 상품 삭제 핸들러 수정
  const handleProductDelete = (deleteData) => {
    // 삭제될 상품 ID
    const deletedProductId = deleteData.cart_option_seq;

    // UI에서 먼저 삭제 (즉시 삭제 효과를 보여주기 위함)
    setCartListData(prev => ({
      bundleMain: prev.bundleMain?.filter(item => item.cart_option_seq !== deletedProductId) || [],
      bundleCp: prev.bundleCp?.filter(item => item.cart_option_seq !== deletedProductId) || [],
      bundleAcc: prev.bundleAcc?.filter(item => item.cart_option_seq !== deletedProductId) || []
    }));

    // selectedProductIds에서도 삭제
    const updatedProductIds = selectedProductIds.filter(id => id !== deletedProductId);
    setSelectedProductIds(updatedProductIds);

    // ref도 즉시 업데이트
    selectedProductIdsRef.current = updatedProductIds;

    // 체크박스 상태 초기화
    setRequestParams(prev => ({
      ...prev,
      point: '',
      allPointUse: 'N',
      balance: '',
      allBalanceUse: 'N',
      maxCouponUse: 'N',
      download_seq: '',
      key: Date.now()
    }));

    // API 호출 파라미터
    const params = {
      ...createRequestParams(updatedProductIds),
      cart_change_goods: [deleteData],
      point: '',
      allPointUse: 'N',
      balance: '',
      allBalanceUse: 'N',
      maxCouponUse: 'N',
      download_seq: ''
    };

    // API 호출
    orderApi.getCartDetail(params)
      .then(response => {
        // 장바구니가 비어있는 경우 또는 성공적으로 응답했지만 상품 목록이 없는 경우
        if (response.data.code === -200 ||
            (response.data.code === 200 &&
             (!response.data.data.cartList ||
              Object.keys(response.data.data.cartList).length === 0 ||
              (!response.data.data.cartList.bundleMain?.length &&
               !response.data.data.cartList.bundleCp?.length &&
               !response.data.data.cartList.bundleAcc?.length)))) {
          // 장바구니가 비었으므로 모든 데이터 초기화
          setCartListData({
            bundleMain: [],
            bundleCp: [],
            bundleAcc: []
          });
          setSelectedProductIds([]);
          selectedProductIdsRef.current = [];
          setOrderSummary({...response.data.data, cartList: {bundleMain: [], bundleCp: [], bundleAcc: []}});

          // 주문 개수 업데이트
          refreshOrderCount();
          return;
        }

        if (response.data.code === 200) {
          const data = response.data.data;

          // 서버 데이터로 상태 업데이트
          setCartListData({
            bundleMain: data.cartList?.bundleMain?.map(item => ({
              ...item,
              isChecked: updatedProductIds.includes(item.cart_option_seq)
            })) || [],
            bundleCp: data.cartList?.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
              ...item,
              isChecked: updatedProductIds.includes(item.cart_option_seq)
            })) || [],
            bundleAcc: data.cartList?.bundleAcc?.map(item => ({
              ...item,
              isChecked: updatedProductIds.includes(item.cart_option_seq)
            })) || []
          });

          // 주문 요약 정보 업데이트
          setOrderSummary(data);

          // 주문 개수 업데이트
          refreshOrderCount();
        }
      })
      .catch(error => {
        console.error('상품 삭제 중 오류:', error);
        // 오류 발생 시 UI 초기화
        setCartListData({
          bundleMain: [],
          bundleCp: [],
          bundleAcc: []
        });
        setSelectedProductIds([]);
        selectedProductIdsRef.current = [];

        // 주문 개수 업데이트
        refreshOrderCount();
      });
  };

  // 선택된 상품 변경 시 API 호출하는 useEffect 수정
  useEffect(() => {
    if (selectedProductIds.length >= 0) { // 0 포함하여 체크
      const timeoutId = setTimeout(() => { // 디바운스 추가
        fetchCartDetail();
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedProductIds)]);

  // 배송지 정보 변경 시 API 호출
  useEffect(() => {
    // 배송지 정보가 있는 경우에만 API 호출
    if (deliveryAddress.zonecode || deliveryAddress.roadAddress || deliveryAddress.detailAddress) {
      fetchCartDetail();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryAddress.zonecode, deliveryAddress.roadAddress, deliveryAddress.detailAddress]);

  // handleSelectedDelete 함수 수정
  const handleSelectedDelete = () => {
    // cartListData에서 모든 상품을 하나의 배열로 합치기
    const allProducts = [
      ...(cartListData.bundleMain || []),
      ...(cartListData.bundleCp || []),
      ...(cartListData.bundleAcc || [])
    ];

    const deleteList = selectedProductIds.map(id => {
      // cartListData에서 해당 cart_option_seq를 가진 상품 찾기
      const product = allProducts.find(item => item.cart_option_seq === id);
      // 상품을 찾으면 해당 ea 값 사용, 없으면 1 (기본값)
      const ea = product?.ea || 1;
      
      return {
        cart_option_seq: id,
        ea: ea,
        delete_flag: 'Y'
      };
    });

    const params = {
      ...createRequestParams(),
      cart_change_goods: deleteList
    };

    debouncedFn(() => {
      orderApi.getCartDetail(params)
        .then(res => {
          // 장바구니가 비어있는 경우 또는 성공적으로 응답했지만 상품 목록이 없는 경우
          if (res.data.code === -200 ||
              (res.data.code === 200 &&
               (!res.data.data.cartList ||
                Object.keys(res.data.data.cartList).length === 0 ||
                (!res.data.data.cartList.bundleMain?.length &&
                 !res.data.data.cartList.bundleCp?.length &&
                 !res.data.data.cartList.bundleAcc?.length)))) {
            // 장바구니가 비었으므로 모든 데이터 초기화
            setCartListData({
              bundleMain: [],
              bundleCp: [],
              bundleAcc: []
            });
            setSelectedProductIds([]);
            selectedProductIdsRef.current = [];
            setOrderSummary({...res.data.data, cartList: {bundleMain: [], bundleCp: [], bundleAcc: []}});

            // 주문 개수 업데이트
            refreshOrderCount();
            return;
          }

          if (res.data.code === 200) {
            const data = res.data.data;
            setCartListData({
              bundleMain: data.cartList.bundleMain?.map(item => ({
                ...item,
                isChecked: false
              })) || [],
              bundleCp: data.cartList.bundleCp?.map(item => ({ // bundleAdd에서 bundleCp로 변경
                ...item,
                isChecked: false
              })) || [],
              bundleAcc: data.cartList.bundleAcc?.map(item => ({
                ...item,
                isChecked: false
              })) || []
            });
            setOrderSummary(data);
            setSelectedProductIds([]);
            setIsAllChecked(false);

            // 주문 개수 업데이트
            refreshOrderCount();
          }
        })
        .catch(err => {
          console.error('선택상품 삭제 실패', err);
          // 오류 발생 시 UI 초기화
          setCartListData({
            bundleMain: [],
            bundleCp: [],
            bundleAcc: []
          });
          setSelectedProductIds([]);
          selectedProductIdsRef.current = [];

          // 주문 개수 업데이트
          refreshOrderCount();
        });
    });
  };

  const handleAddrSelectPopup= () => {
    setPopupAddr(true);
  }
  const handleDelete = (cartOptionSeq) => {
    const deleteData = {
      cart_option_seq: cartOptionSeq,
      ea: 0,
      delete_flag: 'Y'
    };

    handleProductDelete(deleteData);
  };

  // 배송지 선택 핸들러 추가
  const handleAddressConfirm = (selectedAddress) => {
    if (selectedAddress) {
      // 기본 주소로 설정
      setSelectedDelivery('default');

      // deliveryData 상태 업데이트
      setDeliveryData({
        ...deliveryData,
        recipient_name: selectedAddress.recipient_name,
        recipient_phone: selectedAddress.recipient_phone,
        recipient_zipcode: selectedAddress.recipient_zipcode,
        recipient_address_street: selectedAddress.recipient_address_street,
        recipient_address_detail: selectedAddress.recipient_address_detail
      });

      // 기존 deliveryAddress와 recipientInfo 상태도 함께 업데이트
      setDeliveryAddress({
        zonecode: selectedAddress.recipient_zipcode,
        roadAddress: selectedAddress.recipient_address_street,
        detailAddress: selectedAddress.recipient_address_detail
      });

      setRecipientInfo({
        name: selectedAddress.recipient_name,
        phone: selectedAddress.recipient_phone
      });
    }
  };

      // agree_flag 체크를 위한 useEffect (기존 fetchMyInfo API 호출 대신)
      useEffect(() => {
        if (memberTokens && userInfo?.agree_flag === 'R') {
          setIsPopupLoginError({
            message: t("authentication_info_expired"),
            action: 'navigate_profile_edit'
          });
        }
      }, [memberTokens, userInfo, t]);

      const handleConfirm = () => {
        if (isPopupLoginError.action === 'navigate_profile_edit') {
          navigate('/myPage/profile/edit');
        } else if (isPopupLoginError.action === 'navigate_login') {
          navigate('/login');
        } else {
          // window.location.reload();
        }
      }

  // 재고부족 팝업 추가
  const [isStockPopup, setStockPopup] = useState(false);
  const [stockData, setStockData] = useState([]);

  // 재고부족 팝업 확인 핸들러 추가
  const handleStockPopupConfirm = (deleteCartOptionData) => {
    setStockPopup(false);

    // 재고부족 상품을 제외하고 결제 진행
    handlePaymentWithStockShortage(deleteCartOptionData);
  };

  // 재고부족이 있는 경우의 결제 처리 함수 추가
  const handlePaymentWithStockShortage = async (deleteCartOptionData) => {
    // 선택된 상품이 있는지 확인
    if (!selectedProductIds || selectedProductIds.length === 0) {
      setIsPopupLoginError(t('select_payment_product'));
        return;
    }

    // 필수 약관 동의 확인
    if (!Object.values(isChecked).every(Boolean)) {
      setIsPopupLoginError(t('agree_required_terms'));
        return;
    }

    const isValid = orderInfoValidation();
    if (!isValid) return;

    // 결제 요청 데이터 준비 (재고부족 관련 필드 추가)
    const paymentForm = {
        order_seq: "",
        reorder_chk: "N",
        order_type: "parcel",
        rental_store_seq: "",
        return_store_seq: "",
        rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')}`,
        rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')}`,
        download_seq: requestParams.maxCouponUse === 'Y'
            ? orderSummary?.totalInfo?.maxDiscountCouponSeq
            : requestParams.download_seq || "",
        point: requestParams.point || "",
        balance: requestParams.balance || "",
        shipping_cost: orderSummary?.totalInfo?.delivery_price || 0,
        deposit_price: orderSummary?.totalInfo?.deposit_price || 0,
        balance_cash_receipt: requestParams.deposit_cash_receipt || 'N',
        balance_cash_num: requestParams.deposit_cash_num || '',

        depositInfo: {
            deposit_refund_account: "",
            deposit_refund_mname: "",
            deposit_refund_name: "",
            deposit_refund_price: orderSummary?.totalInfo?.deposit_price || 0,
        },
        discountInfo: {
          first_payment_sale: orderSummary?.totalInfo?.first_payment_sale || 0,
          mem_group_sale: orderSummary?.totalInfo?.sumGroupSale || 0,
          mem_type_sale: orderSummary?.totalInfo?.sumMtypeSale || 0
        },
        deliveryInfo: {
            recipient_name: selectedDelivery === 'default' ?
                deliveryData.recipient_name :
                recipientInfo.name,
            recipient_phone: selectedDelivery === 'default' ?
                deliveryData.recipient_phone :
                recipientInfo.phone,
            recipient_zipcode: deliveryAddress.zonecode,
            recipient_address_street: deliveryAddress.roadAddress,
            recipient_address_detail: deliveryAddress.detailAddress,
            recipient_memo: selectedMessage === 'reason6' ?
                customMessage :
                messageList.find(msg => msg.value === selectedMessage)?.name || ""
        },

        insuranceInfo: {
            insurance_seq: requestParams.insurance_seq || 0,
            insurance_repair_per: orderSummary?.insuranceList?.find(
                insurance => insurance.insurance_seq === requestParams.insurance_seq
            )?.insurance_repair_per || 0,
            insurance_price: orderSummary?.totalInfo?.insurance_price || 0
        },

        user_memo: memo,
        pay_select_goods: selectedProductIds.filter(id => {
            const isMainChecked = cartListData.bundleMain?.some(item =>
                item.cart_option_seq === id && item.isChecked);
            const isAddChecked = cartListData.bundleCp?.some(item =>
                item.cart_option_seq === id && item.isChecked);
            const isAccChecked = cartListData.bundleAcc?.some(item =>
                item.cart_option_seq === id && item.isChecked);
            return isMainChecked || isAddChecked || isAccChecked;
        }),
        stock_shortage_flag: "Y", // 재고부족 플래그 추가
        delete_cart_option: deleteCartOptionData // 삭제할 상품 정보 추가
    };

    try {
      const result = await PaymentService.requestPayment(paymentForm);


      if (result.success) {
        const data = result.data;

        // 0원 결제 처리 (문자열 "0"과 숫자 0 모두 처리)
        if (Number(data.price) === 0) {
            const zeroPaymentData = PaymentService.createZeroPaymentData(data);
            setIsPopupLoginError('주문이 완료되었습니다.\n주문정보는 마이페이지에서 확인 가능합니다.');
            navigate(`/order/orderComplete/${zeroPaymentData.orderSeq}`, {
                state: {
                    order_seq: zeroPaymentData.orderSeq,
                    settleprice: zeroPaymentData.settleprice,
                    order_kind: zeroPaymentData.orderKind
                }
            });
            return;
        }

        if (result.isMobile) {
            // 모바일/앱에서는 리다이렉션 방식 사용
            PaymentService.processMobilePayment(data, window.location.origin);
        } else {
            // PC에서는 iframe 방식 사용
            setIsPayment(true);

            setTimeout(() => {
                const iframe = document.getElementById('payment-container');
                if (iframe) {
                    const paymentUrl = PaymentService.createPaymentUrl(data, window.location.origin);
                    iframe.src = paymentUrl;
                } else {
                    console.error('❌ 결제창 iframe을 찾을 수 없습니다.');
                    setIsPopupLoginError('결제창을 열 수 없습니다. 페이지를 새로고침 후 다시 시도해주세요.');
                    setIsPayment(false);
                }
            }, 100);
        }
      } else {
        setIsPopupLoginError(result?.data?.message || '결제 요청에 실패했습니다.');
        setIsPayment(false);
      }
    } catch (error) {
      console.error('재고부족 결제 요청 중 오류:', error);
      setIsPopupLoginError('결제 처리 중 오류가 발생했습니다.');
      setIsPayment(false);
    }
  };

  // 팝업 상태 추가
  const [isTermsPopup, setIsTermsPopup] = useState(false);
  const [popupContent, setPopupContent] = useState({ title: '', content: '' });

  // 약관 보기 버튼 클릭 핸들러 수정
  const handleViewTerms = async (type, event) => {
    event.stopPropagation(); // 이벤트 전파 중지
    event.preventDefault(); // 기본 동작 방지
    
    let title = '';
    let dataKey = '';

    switch(type) {
      case 'order_product_agreement':
        title = '주문 상품에 동의';
        dataKey = 'order_product_agreement';
        break;
      case 'privacy_use_agreement':
        title = '개인정보 수집/이용 동의';
        dataKey = 'privacy_use_agreement';
        break;
      case 'privacy_3rd_agreement':
        title = '개인정보 제3자 제공 동의';
        dataKey = 'privacy_3rd_agreement';
        break;
      case 'pg_service_agreement':
          title = '';
          dataKey = 'pg_service_agreement';
          break;
      default:
        return;
    }

    try {
      const response = await instance.post(TERMS_LIST_URL);
      
      if (response.data.code === 200) {
        setPopupContent({ 
          title, 
          content: response.data.data[dataKey] || '약관 내용을 불러올 수 없습니다.' 
        });
        setIsTermsPopup(true);
      } else {
        throw new Error(response.data.message || '약관 내용을 불러올 수 없습니다.');
      }
    } catch (error) {
      console.error('약관 내용을 불러오는 중 오류 발생:', error);
      setPopupContent({ 
        title, 
        content: '약관 내용을 불러올 수 없습니다. 잠시 후 다시 시도해주세요.' 
      });
      setIsTermsPopup(true);
    }
  };

  // 대표 상담번호 
  const [storePhone, setStorePhone] = useState('');

  useEffect(() => {
    const fetchStorePhone = async () => {
      try {
        const response = await instance.post(FOOTER_DATA_URL);
        setStorePhone(response.data.data.data);
      } catch (error) {
        console.error('대표 상담번호 가져오기 실패:', error);
      }
    };
    fetchStorePhone();
  }, []);


  return(
    <div>
      <div className="order_inner">

        {/* 대여 주문단계 아이콘 */}
        <div className="order_step">
          <ul>
            <li>
              <img src={o_select} alt="order_select"/>
              <p>{t('equipment_selection')}</p>
            </li>
            <li className="step_icon">
              <img src={next} alt="next" loading="lazy"/>
            </li>
            <li>
              <img src={o_order} alt="o_order" loading="lazy"/>
              <p>{t('equipment_order')}</p>
            </li>
            <li className="step_icon">
              <img src={next} alt="next" loading="lazy"/>
            </li>
            <li>
              <img src={o_receive} alt="o_receive" loading="lazy"/>
              <p>{t('equipment_received')}</p>
            </li>
            <li className="step_icon">
              <img src={next} alt="next" loading="lazy"/>
            </li>
            <li>
              <img src={o_rental} alt="o_rental" loading="lazy"/>
              <p>{t('equipment_rented')}</p>
            </li>
            <li className="step_icon">
              <img src={next} alt="next" loading="lazy"/>
            </li>
            <li>
              <img src={o_return} alt="o_rental" loading="lazy"/>
              <p>{t('equipment_returned')}</p>
            </li>
          </ul>
        </div>

        {/* 택배 > 대여기간 */}
        <div className="o_rental_time">
          <div className="order_title">
            <h3>{t('select_rental_period')}</h3>
            <button className="reset_button" onClick={handleReset}>
              {t('initialization')}
            </button>
          </div>

          <div className="rental_box">
            {/* 대여 시작 */}
            <div className="rental_select">
              <div className="rental_label">{t('rental_start_date')}</div>
              <div className="select_info">
                <DatePicker
                  value={rentalDateTime.date}
                  onDateChange={handleRentalDateChange}
                  isDisabled={false}
                  minDate={dayjs().add(3, 'day')}
                  disableHolidays={true}  // 공휴일만 비활성화
                  disableWeekends={true} // 주말은 활성화
                />
              </div>
            </div>
            <span className="o_period">~</span>
            {/* 대여 종료 */}
            <div className="return_select">
              <div className="select_box">
                <p className="rental_label">{t('rental_end_date')}</p>
                <div className="select_info">
                  <DatePicker
                    value={returnDateTime.date}
                    onDateChange={handleReturnDateChange}
                    minDate={rentalDateTime.date}
                    isDisabled={false}
                    disableHolidays={true}  // 공휴일만 비활성화
                    disableWeekends={true} // 주말은 활성화
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex-btw">
            <p className="guide_text_1 g-80">{t('reservation_notice')}</p>
            {/*  총 대여일 - 계산된 값으로 업데이트 */}
            <div
              className="total_rental flex-center-2"
              style={{marginTop:"0"}}
            >
              <span>{t('total_rental_days4')}:</span>
              <p><span className="strong_b1">{totalRentalDays}</span>{t('days_txt')}</p>
            </div>
          </div>
        </div>

        {/* 베송지 정보 */}
        <section className="mt_48">
          <div className="order_title">
            <div className="flex-8">
              <h3>{t('delivery_info')}</h3>
            </div>
            <div style={{font:'var(--body1-1)'}}>
              <span className="reset_text_btn">* </span>{t('required_fields')}
            </div>
          </div>
          <ul className="default_info_table addr_table">
            <li className="info_item addr_radio">
              <div className="label">
                <p>{t('select_delivery_address')} <span className="yellow">*</span></p>
              </div>
              <div className="form_info">
                <div className="info_detail">
                  <div className="default_radio radio_list">
                    {deliveryLIst.map(item =>(
                      <div key={item.id}>
                        <input
                          type="radio"
                          name="selectDelivery"
                          id={item.id}
                          value={item.value}
                          checked={selectedDelivery === item.value}
                          onChange={handleDeliveryChange}
                          />
                        <label htmlFor={item.id}>{item.label}</label>
                      </div>
                    ))}

                    <button
                      type="button"
                      className="default_btn light_gray sm lang_sm"
                      style={{marginLeft:'4px'}}
                      onClick={handleAddrSelectPopup}
                    >
                      {t('view_address_book')}
                    </button>
                  </div>
                </div>
              </div>
            </li>

            {/* 받는 사람 */}
            <li className="info_item addr_receiver">
              <div className="label">
                <label htmlFor="receiver">
                  <p>{t('recipient')} <span className="yellow">*</span></p>
                </label>
              </div>
              <div className="form_info">
                <div className="info_detail">
                  <div className="default_input none_class">
                    <input
                      id="receiver"
                      type="text"
                      value={selectedDelivery === 'default' ?
                        deliveryData.recipient_name || '' :
                        recipientInfo.name}
                      onChange={(e) => {
                        if (selectedDelivery === 'new') {
                          setRecipientInfo(prev => ({
                            ...prev,
                            name: e.target.value
                          }));
                        }
                      }}
                      disabled={selectedDelivery === 'default'}
                      placeholder={t('recipient_name')}
                    />
                  </div>
                </div>
              </div>
            </li>

            {/* 핸드폰 번호 */}
            <li className="info_item addr_receiver">
              <div className="label">
                <p>{t('phone_number')} <span className="yellow">*</span></p>
              </div>
              <div className="form_info">
                <div className="info_detail">
                  <div className="default_input flex-center-8 none_class">
                    <input
                      type="text"
                      value={selectedDelivery === 'default' ?
                        deliveryData.recipient_phone || '' :
                        recipientInfo.phone}
                      onChange={(e) => {
                        if (selectedDelivery === 'new') {
                          setRecipientInfo(prev => ({
                            ...prev,
                            phone: e.target.value
                          }));
                        }
                      }}
                      disabled={selectedDelivery === 'default'}
                      placeholder={t('delivery_address_name3')}
                    />
                  </div>
                </div>
              </div>
            </li>

            {/* 우편번호 찾기 */}
            <li className="info_item addr_edit">
              <div className="label">
                <p>{t('delivery_address')} <span className="yellow">*</span></p>
              </div>
              <div className="form_info">
                <div className="info_detail default_input">
                  <div className="address_input">
                    <div className="zip_code flex-center-4">
                      <input
                        type="text"
                        value={selectedDelivery === 'default' ?
                          deliveryData.recipient_zipcode || '' :
                          deliveryAddress.zonecode}
                        readOnly
                      />
                      <button
                        type="button"
                        className="default_btn sm light_gray"
                        onClick={handlePostcodeOpen}
                        disabled={selectedDelivery === 'default'} // 기본주소일 때 버튼 비활성화
                      >
                        {t('postal_code_search')}
                      </button>
                    </div>
                    <input
                      type="text"
                      value={selectedDelivery === 'default' ?
                        deliveryData.recipient_address_street || '' :
                        deliveryAddress.roadAddress}
                      readOnly
                    />
                    <input
                      type="text"
                      placeholder={t('delivery_address_name9')}
                      value={selectedDelivery === 'default' ?
                        deliveryData.recipient_address_detail || '' :
                        deliveryAddress.detailAddress}
                      onChange={(e) => setDeliveryAddress(prev => ({
                        ...prev,
                        detailAddress: e.target.value
                      }))}
                      disabled={selectedDelivery === 'default'} // 기본주소일 때 입력 비활성화
                    />
                  </div>
                </div>
              </div>
            </li>

            <li className="info_item addr_edit">
              <div className="label">
                <p>{t('delivery_message')}</p>
              </div>
              <div className="form_info flex-wrap">
                <div className="info_detail">
                  <div className={`default_select border ${isMessageOpen ? 'open' : ''}`}>
                    <button
                      type="button"
                      className="select_btn"
                      onClick={() => setIsMessageOpen(!isMessageOpen)}
                    >
                      <span
                        className={`ellipsis_clamp ${!selectedMessage ? 'placeholder' : ''}`}
                      >
                        {selectedMessage ?
                          messageList.find(msg => msg.value === selectedMessage)?.name :
                          '배송 시 요청사항을 선택해 주세요.'
                        }
                      </span>
                    </button>
                    {isMessageOpen && (
                      <ul className="select_list">
                        {messageList.map(msg => (
                          <li
                            key={msg.value}
                            className={`select_item ${selectedMessage === msg.value ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedMessage(msg.value);
                              setIsMessageOpen(false);
                            }}

                          >
                            {msg.name}
                          </li>
                        ))}
                      </ul>
                    )}
                    {selectedMessage === 'reason6' && (
                      <div className="default_input w-100">
                        <textarea
                          value={customMessage}
                          onChange ={(e)=> setCustomMessage(e.target.value)}
                          maxLength={50}
                          placeholder={t('input_content_placeholder')}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </li>
          </ul>

        </section>

        {/* 우편번호 모달 */}
        {isPostcodeOpen && (
          <>
            <div
              className="dimmed"
              onClick={handlePostcodeClose}
              style={{
                position: 'fixed',
                width: '100vw',
                height: '100vh',
                top: '0',
                left: '0',
                background: 'rgba(0, 0, 0, 0.50)',
                zIndex: 101
              }}
            />
            <div className="default_popup">
              <div className="modal_content">
                <div className="modal_wrap">
                  <div className="modal_header">
                    <button
                      type="button"
                      className="close_btn"
                      onClick={handlePostcodeClose}
                    ></button>
                  </div>
                  <div className="modal_body">
                    <DaumPostcodeEmbed
                      onComplete={handlePostcodeComplete}
                      onClose={handlePostcodeClose}
                      style={{ height: '460px' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* 대여상품 선택 */}
        <section className="mt_48 check_pd">
          <div className="order_title">
            <div className="flex-8">
              <h3>{t('select_rental_product_txt')}</h3>
              <p className="count">{selectedCounts.main}<span style={{color:"#8d8d8d"}}>/{cartListData?.bundleMain?.length || 0}</span></p>
            </div>
            <div className="o_btn">
              <button
                type="button"
                className="default_btn black xs"
                onClick={handleSelectedDelete}
              >
                {t('delete_selected_product')}
              </button>
            </div>
          </div>
          <div>
            <ul className="rent order_prod_list">
              {cartListData?.bundleMain && cartListData.bundleMain.length > 0 ? (
                cartListData.bundleMain.map((item, index) => (
                    <li key={item.cart_option_seq || index}
                    style={{
                      borderTop: index > 0 ? '1px solid black' : 'none'
                    }}>
                    <OrderItem
                      item={item}
                      onSelect={(productId, isSelected) => handleProductSelect(productId, isSelected, 'main')}
                      onQuantityChange={handleProductQuantityChange}
                      onDelete={handleProductDelete}
                      openInNewWindow={true}
                    />

                    {/* bundlePkg가 있는 경우에만 패키지 내용물 표시 */}
                    {item.bundlePkg && item.bundlePkg.length > 0 && (
                      <div
                        className={`package_con ${isShow[item.cart_option_seq] ? 'active' : ''}`}
                        onClick={() => handleShow(item.cart_option_seq)}
                      >
                        <div className="package_total flex-justify-center">
                          {/* 패키지 전체보기 버튼 */}
                          <div className="flex-center-2">
                            {t("package")}<span className="strong_ch">{item.bundlePkg.length}</span>
                          </div>
                          {t('find_email_0617')} <i className="arrow_icon"></i>
                        </div>

                        {/* 패키지 내용물 매핑 */}
                        {item.bundlePkg.map((pkgItem, pkgIndex) => (
                          <div className="package_item" key={pkgItem.cart_option_seq || `pkg-${pkgIndex}`}>
                            <div className="default_chk_box"></div>
                            <div className="prod_container">
                              <img
                                src={pkgItem.goods_image || product}
                                alt="상품 이미지"
                                className="order_prod_img"
                                loading="lazy"
                              />
                              <div className="prod_info">
                                <div className="info_box">
                                  <div className="prod_title">
                                    <p className="title ellipsis1">{pkgItem.product_name || 'ARRI ALEXA Mini Full'}</p>
                                    <p className="sub_title ellipsis1">{pkgItem.description_kr_01 || 'PL Mountd PL Mountd PL Mountd'}</p>
                                    <p className="option_count">× {pkgItem.ea}</p>
                                  </div>
                                </div>

                                <div className="prod_count_box">
                                  <div>{/* 빈박스 */}</div>
                                  {pkgItem?.stock_flag === 'N' && (
                                    <div className="stock_btn">
                                      <span className="mobile-text">{t('stock_shortage')}</span>
                                    </div>
                                  )}
                                </div>

                                <div className="total_price">
                                  <button
                                    type="button"
                                    className="delete_btn"
                                    onClick={() => handleDelete(pkgItem.cart_option_seq)}
                                  ></button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                      {/* bundleCp가 있는 경우에만 구성품 내용물 표시 */}
                      {item.bundleCp && item.bundleCp.length > 0 && (
                      <div
                        className={`package_con ${isShow[item.cart_option_seq] ? 'active' : ''}`}
                        onClick={() => handleShow(item.cart_option_seq)}
                      >
                        <div className="package_total flex-justify-center">
                          {/* 구성품 전체보기 버튼 */}
                          <div className="flex-center-2">
                            구성품<span className="strong_ch">{item.bundleCp.length}</span>
                          </div>
                          {t("find_email_0617")}<i className="arrow_icon"></i>
                        </div>

                        {/* 구성품 내용물 매핑 */}
                        {item.bundleCp.map((CpItem, pkgIndex) => (
                          <div className="package_item" key={CpItem.cart_option_seq || `pkg-${pkgIndex}`}>
                            <div className="default_chk_box"></div>
                            <div className="prod_container">
                              <img src={CpItem.goods_image || product}
                                alt="상품 이미지"
                                className="order_prod_img"
                                loading="lazy"
                              />
                              <div className="prod_info">
                                <div className="info_box">
                                  <div className="prod_title">
                                    <p className="title ellipsis1">{CpItem.product_name || 'ARRI ALEXA Mini Full'}</p>
                                    <p className="sub_title ellipsis1">{CpItem.description_kr_01 || 'PL Mountd PL Mountd PL Mountd'}</p>
                                    <p className="option_count">× {CpItem.ea}</p>
                                  </div>
                                </div>

                                <div className="prod_count_box">
                                  <div>{/* 빈박스 */}</div>
                                  {CpItem?.stock_flag === 'N' && (
                                    <div className="stock_btn">
                                      <span className="mobile-text">{t('stock_shortage')}</span>
                                    </div>
                                  )}
                                </div>

                                <div className="total_price">
                                  <button
                                    type="button"
                                    className="delete_btn"
                                    onClick={() => handleDelete(CpItem.cart_option_seq)}
                                  ></button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}

                      </div>
                    )}
                  </li>
                ))
              ) : (
                <div className="no_result">
                  <p>{t('rental_product_empty')}</p>
                </div>
              )}
            </ul>

            {/* 총 상품 수량, 상품 금액 */}
            <div className="total_product flex-center-8" style={{flexDirection:'column', alignItems:'end'}}>
              <div className="flex-center-8">
                <div className="flex-center-2">
                  {t('total_product_quantity2')}
                  <p><span className="strong_b1">{orderSummary?.totalInfo?.total_rental_ea || 0}</span>{t('quantity_count_txt')}</p>
                </div>
                <div className="flex-center-2">
                  {t('total_product_quantity4_0617')}
                  <p className="cap2_1">₩<span className="strong_b1">
                    {orderSummary?.totalInfo?.sumBundleMainPrice ? formatLocalString(orderSummary?.totalInfo?.sumBundleMainPrice) : 0}
                  </span></p>
                </div>
              </div>
              <span className="text_small_b g-80">{t("payment_final_amount_notice")}</span>
            </div>
          </div>
        </section>


        {/* 구성품 선택 */}
        {cartListData?.bundleCp && cartListData.bundleCp.length > 0 && (
        <section className="mt_48 check_pd">
          <div className="order_title">
            <div className="flex-8">
              <h3>구성품 선택</h3>
              <p className="count">{selectedCounts.add}<span style={{color:"#8d8d8d"}}>/{cartListData?.bundleCp?.length || 0}</span></p>
            </div>
            <div className="o_btn">
              <button
                type="button"
                className="default_btn black xs"
                onClick={handleSelectedDelete}
              >
                {t('delete_selected_product')}
              </button>
            </div>
          </div>
          <div>
            <ul className="rent order_prod_list">
              {cartListData?.bundleCp && cartListData.bundleCp.length > 0 ? ( // bundleAdd에서 bundleCp로 변경
                cartListData.bundleCp.map((item, index) => ( // bundleAdd에서 bundleCp로 변경
                  <li key={item.cart_option_seq || index}>
                    <OrderItem
                      item={item}
                      onSelect={(productId, isSelected) => handleProductSelect(productId, isSelected, 'add')}
                      onQuantityChange={handleProductQuantityChange}
                      onDelete={handleProductDelete}
                    />
                  </li>
                ))
              ) : (
                <div className="no_result">
                  <p>{t('no_additional_items_0617')}</p>
                </div>
              )}
            </ul>

            {/* 총 상품 수량, 상품 금액 */}
            <div className="total_product flex-center-8">
              <div className="flex-center-2">
                {t('total_product_quantity2')}
                <p><span className="strong_b1">{orderSummary?.totalInfo?.total_add_ea || 0}</span>{t('quantity_count_txt')}</p>
              </div>
              <div className="flex-center-2">
                {t('total_product_quantity4_0617')}
                <p className="cap2_1">₩<span className="strong_b1">
                  {orderSummary?.totalInfo?.sumBundleAddPrice || 0}
                </span></p>
              </div>
            </div>
          </div>
        </section>
        )}


        {/* 무상품목 선택 */}
        {cartListData?.bundleAcc && cartListData.bundleAcc.length > 0 && (
        <section className="mt_48 check_pd acc_section">
          <div className="order_title">
            <div className="flex-8">
              <h3>{t('acc_info_txt')}</h3>
              <p className="count">{selectedCounts.acc}<span style={{color:"#8d8d8d"}}>/{cartListData?.bundleAcc?.length || 0}</span></p>
            </div>
            <div className="o_btn">
              <button
                type="button"
                className="default_btn black xs"
                onClick={handleSelectedDelete}
              >
                {t('delete_selected_product')}
              </button>
            </div>
          </div>
          <div>
            <ul className="rent order_prod_list">
              {cartListData?.bundleAcc && cartListData.bundleAcc.length > 0 ? (
                cartListData.bundleAcc.map((item, index) => (
                  <li key={item.cart_option_seq || index}>
                    <OrderItem
                      item={item}
                      onSelect={(productId, isSelected) => handleProductSelect(productId, isSelected, 'acc')}
                      onQuantityChange={handleProductQuantityChange}
                      onDelete={handleProductDelete}
                    />
                  </li>
                ))
              ) : (
                <div className="no_result">
                  <p>{t('no_accessory_0617')}</p>
                </div>
              )}
            </ul>

            {/* 총 상품 수량, 상품 금액 */}
            <div className="total_product flex-center-8">
              <div className="flex-center-2">
                {t('total_product_quantity2')}
                <p><span className="strong_b1">{orderSummary?.totalInfo?.total_acc_ea || 0}</span>{t('quantity_count_txt')}</p>
              </div>
            </div>
          </div>
        </section>
        )}


        {/* 결제금액 */}
        <section className="mt_48">
          <div className="order_title">
            <h3>{t('payment_amount')}</h3>
          </div>

          <div>
            <OrderPayment
              key={paymentResetKey}
              orderSummary={orderSummary}
              onUpdateParams={handleUpdateParams}
              requestParams={requestParams}
              orderType={"parcel"}
            />
          </div>
        </section>

        {/* 요청사항 */}
        <section className="mt_48 container request_input">
          <div className="order_title">
            <h3>{t('request_txt')}</h3>
            <p className="text_small_b">{t('need_help_0617')}
              <span className="strong_b3 black">02-{storePhone?.csMainPhone?.value}</span>
              {t('need_help_0617_2')}
            </p>
          </div>
          <div className="default_input">
            <textarea
              className="form_input"
              value={memo}
              onChange={handleMemoChange}
              placeholder={t('request_placeholder')}
            />
          </div>
        </section>
      </div>


      {/* 결제정보표시 */}
      <div className="total_payment_con">
        <div className="order_info_box">
          <div className="order_left">
            <div className="top_con flex-8 flex-start">
              <i className="p_icon">P</i>
              <p className="point_info">
                <span>
                  {t('normal_rental_and_return_txt')} {orderSummary?.pointSetting?.payStar1}%(₩{orderSummary?.pointSetting?.payStarMin ? formatLocalString(orderSummary?.pointSetting?.payStarMin) : '0'}) ~ {orderSummary?.pointSetting?.payStar5}%(₩{orderSummary?.pointSetting?.payStarMax ? formatLocalString(orderSummary?.pointSetting?.payStarMax) : '0'}) {t('point_txt')}
                </span>
                <button
                  className="text_small g-80"
                  type="button"
                  onClick={handleEquipmentPopup}
                >
                  {t('equipment_rating_info')}
                </button>
              </p>
            </div>
            <div className="bottom_con">
              <div className="join_con">
                <div className="default_chk_box all_chk">
                  <input
                    type="checkbox"
                    id="allChk"
                    checked={isAllChecked}
                    onChange={handleAllCheck}
                  />
                  <label htmlFor="allChk">
                    <span> {t('order_info_check_0617')} </span>
                  </label>
                </div>

                <ul className="flex-2 column g-90">
                  <li className="default_chk_box">
                    <input
                      type="checkbox"
                      id="useAgree1"
                      name="option1"
                      checked={isChecked.option1}
                      onChange={() => handleCheck('option1')}
                    />
                    <label htmlFor="useAgree1">
                      <span className="flex-btw">
                        {t('order_product_agree_0617')}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('order_product_agreement', e)}>{t('view')}</div>
                      </span>
                    </label>
                  </li>

                  <li className="default_chk_box">
                    <input
                      type="checkbox"
                      id="useAgree2"
                      name="option2"
                      checked={isChecked.option2}
                      onChange={() => handleCheck('option2')}
                    />
                    <label htmlFor="useAgree2">
                      <span className="flex-btw">
                        {t('order_personal_info_agree_0617')}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('privacy_use_agreement', e)}>{t('view')}</div>
                      </span>
                    </label>
                  </li>

                  <li className="default_chk_box">
                    <input
                      type="checkbox"
                      id="personalAgree3"
                      name="option3"
                      checked={isChecked.option3}
                      onChange={() => handleCheck('option3')}
                    />
                    <label htmlFor="personalAgree3">
                      <span className="flex-btw">
                        {t('order_personal_info_agree_0617_2')}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('privacy_3rd_agreement', e)}>{t('view')}</div>
                      </span>
                    </label>
                  </li>

                  <li className="default_chk_box">
                    <input
                      type="checkbox"
                      id="personalAgree4"
                      name="option4"
                      checked={isChecked.option4}
                      onChange={() => handleCheck('option4')}
                    />
                    <label htmlFor="personalAgree4">
                      <span className="flex-btw">
                        {t('order_payment_service_agree_0617')}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('pg_service_agreement', e)}>{t('view')}</div>
                      </span>
                    </label>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="order_right">
            <div className="total_detail">
            <li className="flex-btw">
                {t("deposit_amount")}
                    <p> ₩ <span>{formatLocalString(orderSummary?.totalInfo?.deposit_price || 0)}</span></p>
                  </li>
                  <li className="flex-btw">
                      {t("insurance_amount")}
                    <p> ₩ <span>{formatLocalString(orderSummary?.totalInfo?.insurance_price || 0)}</span></p>
                  </li>
                  <li className="flex-btw">
                    총 상품 금액
                    <p> ₩ <span>{formatLocalString(orderSummary?.totalInfo?.sumRentalPrice || 0)}</span></p>
                  </li>
                  <li className="flex-btw">
                    총 할인 금액
                    <div style={{display:'flex', alignItems:'center', gap:'20px'}}>
                      <p style={{color:'green'}}>{orderSummary?.totalInfo?.discount_rate}%</p>
                      <p> ₩ <span>{formatLocalString(orderSummary?.totalInfo?.sumSalePrice || 0)}</span></p>
                    </div>
                  </li>
              <div className="total_day">
                <div className="flex-btw">
                  {t('total_rental_days_txt')}
                  <p className="text_small">
                    ({rentalDateTime.date.format('YYYY.MM.DD')}~{returnDateTime.date.format('YYYY.MM.DD')})
                    <span className="strong_b1">{totalRentalDays}</span>{t('total_rental_days_count_0417')}
                  </p>
                </div>
              </div>
            </div>
            <div className="total_amount">
              <div className="flex-btw price_box">
                <p className="label red">{t('total_payment_amount_0617')}:</p>
                <p className="o_total_price red">
                  ₩<span>{formatLocalString(orderSummary?.totalInfo?.settlePrice || 0)}</span>
                </p>
              </div>
              <p
                className="text_small_b g-70"
                style={{textAlign:'right'}}
              >
                ₩{formatLocalString(orderSummary?.totalInfo?.settlePrice || 0)}{t('total_payment_amount_0617_2')}
              </p>
            </div>

            <div className="payment_btn">
              <button
                type="button"
                className="pay_yellow_btn won"
                onClick={handlePayment}
              >
                {formatLocalString(orderSummary?.totalInfo?.settlePrice || 0)} {t('pay_now')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PC 결제창 모달 */}
      {isPayment && (
        <div id="payment" style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 102
        }}>
          <div className="dimmed" style={{
            position: 'fixed',
            width: '100vw',
            height: '100vh',
            top: 0,
            left: 0,
            background: 'rgba(0, 0, 0, 0.50)',
            zIndex: 101
          }} />
          <iframe
            id="payment-container"
            name="paymentIframe"
            title="이니시스 결제창"
            style={{
              width: '820px',
              height: '600px',
              position: 'relative',
              zIndex: 1000,
              border: 'none'
            }}
          />
        </div>
      )}
      <AddressSelectPopup
        isPopup={isPopupAddr}
        setIsPopup={setPopupAddr}
        deliveryData={deliveryData}
        onConfirm={handleAddressConfirm}
      />

      <EquipmentRatingPopup
        isPopup={isEquipmentPopup}
        setIsPopup={setEquipmentPopup}
      />

      <PopupLoginError
        isPopup={isPopupLoginError}
        setIsPopup={setIsPopupLoginError}
        onConfirm={handleConfirm}
      />

      {/* 재고부족 팝업 추가 */}
      <ProductStockPop
        isPopup={isStockPopup}
        setIsPopup={setStockPopup}
        stockData={stockData}
        onConfirm={handleStockPopupConfirm} // 새로운 핸들러 사용
        onCancel={() => setStockPopup(false)}
      />

      {/* 약관 팝업 */}
      <TermsPopup 
        isPopup={isTermsPopup}
        setIsPopup={setIsTermsPopup}
        title={popupContent.title}
        content={popupContent.content}
      />

    </div>
  )
};
export default ParcelOrder;
